import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { ApiError } from '@/types'
import { createMockAdapter } from '@/lib/devMock'

// ── Axios instances ────────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

// Dev mock — activated by VITE_MOCK=true in .env
const mockAdapter = import.meta.env.VITE_MOCK === 'true' ? createMockAdapter() : undefined

/** Primary API client — all authenticated requests go through this instance. */
const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  ...(mockAdapter ? { adapter: mockAdapter } : {}),
})

/**
 * Bare instance used only for the token-refresh call.
 * Must NOT share interceptors with `api` to avoid refresh loops.
 */
const refreshApi = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  ...(mockAdapter ? { adapter: mockAdapter } : {}),
})

// ── Helpers ────────────────────────────────────────────────────────────────────

interface RawApiError {
  success: false
  error: { code: string; message: string }
}

interface TokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
}

function toApiError(err: AxiosError): ApiError {
  if (!err.response) {
    return new ApiError('NETWORK_ERROR', 'Cannot reach the server. Check your connection.')
  }
  const body = err.response.data as RawApiError | undefined
  const code = body?.error?.code ?? 'UNKNOWN'
  const message = body?.error?.message ?? 'An unexpected error occurred. Please try again.'
  return new ApiError(code, message)
}

function clearSession(): void {
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  window.dispatchEvent(new CustomEvent('auth:session-expired'))
}

// ── Request interceptor: attach Bearer token ───────────────────────────────────

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

// ── Response interceptor: normalise errors + silent token refresh ──────────────

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

// Auth endpoints that must never trigger the silent-refresh retry loop
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout']

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as RetryableConfig | undefined

    // Skip retry for auth endpoints — a 401 on /auth/login means wrong password,
    // not an expired session, so we must not clear tokens or call clearSession().
    const url = original?.url ?? ''
    const isAuthEndpoint = AUTH_ENDPOINTS.some((p) => url.endsWith(p))

    if (err.response?.status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true
      const refreshToken = localStorage.getItem('refresh_token')

      if (!refreshToken) {
        clearSession()
        return Promise.reject(toApiError(err))
      }

      try {
        const { data } = await refreshApi.post<TokenResponse>('/auth/refresh', {
          refresh_token: refreshToken,
        })
        // Per spec: always store BOTH new tokens (refresh token is rotated)
        localStorage.setItem('access_token', data.access_token)
        localStorage.setItem('refresh_token', data.refresh_token)
        window.dispatchEvent(new CustomEvent('auth:token-refreshed'))
        // Retry original request with new token
        original.headers['Authorization'] = `Bearer ${data.access_token}`
        return api(original)
      } catch {
        clearSession()
        return Promise.reject(toApiError(err))
      }
    }

    return Promise.reject(toApiError(err))
  }
)

export default api
